import { useApi } from "@api/ApiProvider";
import type { SessionMode } from "@constants/documentCategories";
import { useWorkingDocumentId, type WorkingDocumentId } from "@hooks/useWorkingDocumentId";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import useSessionStorageState from "use-session-storage-state";

import { createSessionApi, deleteSessionApi, listSessionsApi } from "./sessions.api";
import type { Session } from "./sessions.types";

// Left as a single prefix const, not a query-key factory (cf. documentsQueryKeyBySession):
// used only within this file, so there's no cross-file drift risk — no need to refactor early.
const SESSIONS_QUERY_KEY = "sessions";
// Named only for parity with the other *_STORAGE_KEY consts. Single use, so this is
// otherwise an early refactor — that's why one-off keys are left inline elsewhere.
const SESSIONS_MAP_STORAGE_KEY = "sessionsMap";

// Stable references for the "no data yet" cases. Recreating these inline on
// every render makes downstream useMemo / useEffect deps churn and can drive
// consumers (e.g. SessionSelection's `[options]` effect) into infinite loops.
const EMPTY_SESSIONS_MAP: Record<WorkingDocumentId, Session> = {};
const EMPTY_SESSIONS: Session[] = [];

export type CreateSessionInput = { name: string; mode: SessionMode };

export const useSessions = () => {
  const { documentId: workingDocumentId } = useWorkingDocumentId();
  const { api } = useApi();
  const queryClient = useQueryClient();

  // storageSync off keeps session state isolated to this taskpane instance.
  const [sessionsMap, setSessionsMap] = useSessionStorageState<Record<WorkingDocumentId, Session>>(
    SESSIONS_MAP_STORAGE_KEY,
    { defaultValue: EMPTY_SESSIONS_MAP, storageSync: false }
  );

  const currentSession = useMemo(() => {
    if (!workingDocumentId) return null;
    return sessionsMap[workingDocumentId] ?? null;
  }, [workingDocumentId, sessionsMap]);

  const setCurrentSession = useCallback(
    (session: Session | null) => {
      if (!workingDocumentId) return;
      setSessionsMap((prev = EMPTY_SESSIONS_MAP) => {
        const existing = prev[workingDocumentId] ?? null;
        // Bail out if the value is unchanged — prevents redundant writes that
        // would invalidate sessionsMap reference and cascade re-renders.
        if (existing === session) return prev;
        if (session === null) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [workingDocumentId]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [workingDocumentId]: session };
      });
    },
    [workingDocumentId, setSessionsMap]
  );

  const { data, isLoading: isLoadingSessions } = useQuery({
    queryKey: [SESSIONS_QUERY_KEY, workingDocumentId],
    queryFn: () => listSessionsApi(api, workingDocumentId!),
    enabled: !!workingDocumentId,
  });

  const sessions = data ?? EMPTY_SESSIONS;

  const createSessionMutation = useMutation<Session, unknown, CreateSessionInput>({
    mutationFn: async ({ name, mode }) => {
      const { session_id, session_name, session_mode } = await createSessionApi(api, {
        documentId: workingDocumentId!,
        sessionMode: mode,
        sessionName: name,
      });
      return {
        session_id,
        session_name,
        session_mode,
        document_id: workingDocumentId!,
      };
    },
    onSuccess: (newSession) => {
      queryClient.setQueryData<Session[]>([SESSIONS_QUERY_KEY, workingDocumentId], (old = []) => {
        const exists = old.some((s) => s.session_id === newSession.session_id);
        return exists ? old : [...old, newSession];
      });
      // Note: we deliberately do NOT call setCurrentSession here. The creation
      // flow flips the active session at the very end — after any post-create
      // server work (e.g. Office Action prior-art upload) has settled — so the
      // documents query (keyed on sessionId) only fetches once and sees the
      // server in its final state.
    },
  });

  const deleteSessionMutation = useMutation<void, unknown, string>({
    mutationFn: (sessionId) => deleteSessionApi(api, sessionId),
    onSuccess: (_result, sessionId) => {
      queryClient.invalidateQueries({ queryKey: [SESSIONS_QUERY_KEY, workingDocumentId] });
      if (currentSession?.session_id === sessionId) setCurrentSession(null);
    },
  });

  const loadSession = useCallback(
    (sessionId: string): Session | null => {
      const found = sessions.find((s) => s.session_id === sessionId) ?? null;
      if (found) setCurrentSession(found);
      return found;
    },
    [sessions, setCurrentSession]
  );

  return {
    sessions,
    session: currentSession,
    sessionId: currentSession?.session_id ?? null,
    sessionMode: currentSession?.session_mode ?? null,
    isLoadingSession: isLoadingSessions || deleteSessionMutation.isPending,
    /** Returns the loaded session, or null if not found in the current list. */
    loadSession,
    /** Single-concern mutation for creating a session. The composed creation
     * flow (session + optional Office Action preload) lives in
     * `SessionCreationFlow`. */
    createSession: createSessionMutation,
    /** Single-concern mutation for deleting a session. */
    deleteSession: deleteSessionMutation,
    /** Imperative setter for the active session. The creation flow uses this
     * to flip the active session AFTER any post-create server work has
     * finished. */
    setCurrentSession,
  };
};
